"use client";

import { useEffect, useState, useRef } from "react";
import { Modal, Progress, Button, Row, Col, message, Select } from "antd";
import { getOAuthHeaders } from ".././constants/oAuthValidation";
import { upsertOfdOrderStatus } from "../services/index";
import messages from "../errors/messages.en-IN.json";

const { Option } = Select;

const OrderNotification = ({ orderData, index, onAutoAccept, onReject, onClose }) => {
  const authHeaders = getOAuthHeaders();
  const [visible, setVisible] = useState(true);
  const [progress, setProgress] = useState(100);
  const [loading, setLoading] = useState(false);
  const [showRejectOptions, setShowRejectOptions] = useState(false);
  const [selectedReason, setSelectedReason] = useState(null);
  const countdownTime = 30;
  const intervalRef = useRef(null);

  const rejectionReasons = [
    "item_out_of_stock",
    "store_closed",
    "store_busy",
    "rider_not_available",
    "out_of_delivery_radius",
    "connectivity_issue",
    "total_missmatch",
    "invalid_item",
    "option_out_of_stock",
    "invalid_option",
    "unspecified",
  ];

  const clearCountdown = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  useEffect(() => {
    if (visible && orderData?.data) {
      const audio = new Audio("/sounds/order.mp3");
      audio.loop = true; // Enable looping
      audio.play().catch((err) => {
        console.warn("Autoplay prevented:", err);
      });

      // Cleanup: Stop the audio when modal is closed or component unmounts
      return () => {
        audio.pause(); // Stop the audio
        audio.currentTime = 0; // Reset to start
      };
    }
  }, [visible, orderData]);

  useEffect(() => {
    if (!orderData?.data) return;

    let remainingTime = countdownTime;
    intervalRef.current = setInterval(() => {
      remainingTime -= 1;
      setProgress((remainingTime / countdownTime) * 100);

      if (remainingTime <= 0) {
        clearCountdown();
        handleAccept(true);
      }
    }, 1000);

    return () => clearCountdown();
  }, [orderData]);

  const updateOrderStatus = async (newStatus, reason = null, isOnline = "N") => {
    const setAuthTokens = authHeaders.access_token;
    const statusMapping = {
      Acknowledged: "AK",
      Cancelled: "CC",
      "Food Ready": "FR",
      Dispatched: "DI",
    };
    const statusCode = statusMapping[newStatus] || newStatus;
    setLoading(true);
    try {
      console.debug("[ORDER-ACCEPT-DIAG] updateOrderStatus → calling upsertOfdOrderStatus", { orderId: orderData?.data?.orderId, statusCode, reason, isOnline });
      const response = await upsertOfdOrderStatus(orderData?.data.orderId, statusCode, reason, null, isOnline);

      const result = response?.data?.data?.upsertOfdOrderStatus;
      console.debug("[ORDER-ACCEPT-DIAG] upsertOfdOrderStatus response", { status: result?.status, message: result?.message });
      if (result?.status === "200") {
        message.success(result?.message);
      } else {
        console.error("Status update failed:", result?.message);
        message.error(`${messages.errors.orders.status_update_failed}`);
      }
    } catch (error) {
      console.error("Error updating order status:", error);
      message.error(`${messages.errors.orders.status_update_failed}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (autoAccept) => {
    console.debug("[ORDER-ACCEPT-DIAG] handleAccept invoked", { autoAccept, orderId: orderData?.data?.orderId, externalId: orderData?.data?.externalId });
    clearCountdown();
    setVisible(false);
    if (!autoAccept) {
      await updateOrderStatus("Acknowledged");
    }
    if (onAutoAccept) {
      onAutoAccept({ isManualAccept: !autoAccept });
    }
  };

  const handleRejectClick = () => {
    setShowRejectOptions(true);
  };

  const handleRejectConfirm = async () => {
    if (!selectedReason) {
      message.error(`${messages.errors.orders.rejection_reason_required}`);
      return;
    }
    clearCountdown();
    setVisible(false);
    await updateOrderStatus("Cancelled", selectedReason);
    if (onReject) {
      onReject();
    }
    setShowRejectOptions(false);
    setSelectedReason(null);
  };
  const handleClose = () => {
    clearCountdown();
    setVisible(false);
    if (onClose) {
      onClose();
    }
  };

  if (!orderData?.data) return null;

  return (
    <Modal
      open={visible}
      footer={null}
      closable={true}
      onCancel={handleClose}
      mask={false}
      centered={false}
      width={300}
      style={{
        top: 10 + index * 320,
        left: "50%",
        transform: "translateX(-50%)",
        position: "absolute",
        paddingTop: "0px",
        overflow: "hidden",
      }}
      styles={{ body: {
        padding: "20px 15px",
        paddingTop: "0px",
        borderRadius: "10px",
      } }}
    >
      <div style={{ position: "absolute", top: 0, left: 0, width: "100%" }}>
        <Progress percent={progress} status="active" showInfo={false} strokeColor="#2F3856" strokeWidth={5} style={{ marginBottom: "0px", borderRadius: "10px 10px 0 0" }} />
      </div>

      <div style={{ textAlign: "left", padding: "20px 10px 10px" }}>
        <h3 style={{ fontSize: "16px", marginBottom: "5px", color: "#0F0718" }}>New Order #{orderData.data.orderNo}</h3>
        <p style={{ fontSize: "12px", marginBottom: "15px", color: "#504F53" }}>Order will be auto-confirmed in 30 seconds.</p>

        {showRejectOptions ? (
          <>
            <Select style={{ width: "100%", marginBottom: "15px" }} placeholder="Select rejection reason" onChange={(value) => setSelectedReason(value)} value={selectedReason}>
              {rejectionReasons.map((reason) => (
                <Option key={reason} value={reason}>
                  {reason.replace(/_/g, " ")}
                </Option>
              ))}
            </Select>
            <Row justify="center" gutter={10}>
              <Col>
                <Button
                  type="default"
                  size="large"
                  onClick={() => setShowRejectOptions(false)}
                  style={{
                    color: "#2F3856",
                    background: "#FFFFFF",
                    borderRadius: "15px",
                    fontSize: "14px",
                    padding: "4px 8px",
                    border: "1px solid #2F3856",
                  }}
                >
                  Cancel
                </Button>
              </Col>
              <Col>
                <Button
                  type="primary"
                  size="large"
                  onClick={handleRejectConfirm}
                  loading={loading}
                  style={{
                    color: "#FFFFFF",
                    background: "#2F3856",
                    borderRadius: "15px",
                    fontSize: "14px",
                    padding: "4px 8px",
                    border: "none",
                  }}
                >
                  Confirm Reject
                </Button>
              </Col>
            </Row>
          </>
        ) : (
          <Row justify="center" gutter={10}>
            <Col>
              <Button
                type="default"
                size="large"
                onClick={handleRejectClick}
                loading={loading}
                style={{
                  color: "#2F3856",
                  background: "#FFFFFF",
                  borderRadius: "15px",
                  fontSize: "14px",
                  padding: "4px 8px",
                  border: "1px solid #2F3856",
                }}
              >
                Reject
              </Button>
            </Col>
            <Col>
              <Button
                type="primary"
                size="large"
                onClick={() => {
                  handleAccept(false);
                }}
                loading={loading}
                style={{
                  color: "#FFFFFF",
                  background: "#2F3856",
                  borderRadius: "15px",
                  fontSize: "14px",
                  padding: "4px 8px",
                  border: "none",
                }}
              >
                Accept
              </Button>
            </Col>
          </Row>
        )}
      </div>
    </Modal>
  );
};

export default OrderNotification;
